import JsBarcode from 'jsbarcode';
import dayjs from './dayjs';

export function evaluateFlexibleUnitConversion(conversionString, x, x1 = null, x2 = null) {
    // Determine the precision of the input value(s)
    const getPrecision = (value) => {
      if (value === null || value === undefined) return 0;
      if (Number.isInteger(parseFloat(value))) return 2; // Default to 2 for integers
      const decimalPart = value.toString().split('.')[1];
      return decimalPart ? decimalPart.length : 0;
    };
  
    const precision = Math.max(getPrecision(x), getPrecision(x1), getPrecision(x2));
  
    // Split the string into expression and unit
    const [expression, unit] = conversionString.split('=')?.map(part => part.trim());
    
    // Function to evaluate the expression for a given value
    const evaluateForValue = (value) => {
      if (value === null || value === undefined) return null;
      const preparedExpression = expression.replace(/x/g, value);
      try {
        const result = Function('"use strict";return (' + preparedExpression + ')')();
        const factor = Math.pow(10, precision);
        return Math.round(result * factor) / factor;
      } catch (error) {
        console.error("Error evaluating expression for value =", value, ":", error);
        return null;
      }
    };
  
    // Evaluate for x, x1, and x2
    const convertedX = evaluateForValue(x);
    const convertedX1 = evaluateForValue(x1);
    const convertedX2 = evaluateForValue(x2);
  
    // Format the results
    const formatValue = (value) => value === null ? null : parseFloat(value.toFixed(precision));
  
    // Construct the return object
    let result = {
      convertedValue: formatValue(convertedX),
      convertedUnit: unit,
      convertedReferenceRange: null
    };
  
    if (x1 !== null || x2 !== null) {
      let lowerBound = convertedX1 !== null ? convertedX1 : convertedX;
      let upperBound = convertedX2 !== null ? convertedX2 : convertedX;
      
      // Ensure lower bound is actually lower
      if (lowerBound > upperBound) {
        [lowerBound, upperBound] = [upperBound, lowerBound];
      }
      
      result.convertedReferenceRange = `[${formatValue(lowerBound)} - ${formatValue(upperBound)}]`;
    }
  
    return result;
  }
  
  // convert to date from {day:month, month:month, year:year}
// to 2023-12-23T11:25:50Z like format
export function convertToISODate(date) {
    try {
        const dateObj = new Date(date.year, date.month - 1, date.day);
        if (isNaN(dateObj.getTime())) {
            throw new Error("Invalid date");
        }
        return dateObj.toISOString();
    } catch (error) {
        // empty date field
        return null;
    }
}

// function that generates inital object like newPatient from the Dto 
export function generateInitialObject(dto) {
    const initialObject = {};
    for (const [key, value] of Object.entries(dto)) {
        const lowerCaseKey = key.charAt(0).toLowerCase() + key.slice(1);
        switch (value) {
            case "String":
                initialObject[lowerCaseKey] = "";
                break;
            case "DateTime":
                initialObject[lowerCaseKey] = new Date().toISOString();
                break;
            case "Int":
                initialObject[lowerCaseKey] = 0;
                break;
            case "Double":
                initialObject[lowerCaseKey] = 0.0;
                break;
            case "Bool":
                initialObject[lowerCaseKey] = false;
                break;
            case "List`1":
                initialObject[lowerCaseKey] = [];
                break;
            default:
                initialObject[lowerCaseKey] = null;
                break;
        }
    }
    return initialObject;
}


/** Adapts a FormResponseDto into the shape FormBasedEntityView/Edit expect from a generic entity. */
export function mapFormResponseDtoToEntity(fr) {
    if (!fr || fr.id == null) return null;
    return {
        ...fr,
        id: fr.id,
        defaultFormId: fr.formId,
        formResponseId: fr.id,
        enabled: (fr.status || '').toLowerCase() === 'completed',
    };
}

export function convertToComponentDate(date) {
    const dateObj = new Date(date);
    const day = dateObj.getDate();
    const month = dateObj.getMonth() + 1;
    const year = dateObj.getFullYear();
    const componentDate = {
        day: day,
        month: month,
        year: year,
    };
    return componentDate;
}

export const styles = {
    csvReader: {
        display: 'flex',
        flexDirection: 'row',
        marginBottom: 10,
    },
    browseFile: {
        width: '20%',
    },
    acceptedFile: {
        border: '1px solid #ccc',
        height: 45,
        lineHeight: 2.5,
        paddingLeft: 10,
        width: '80%',
    },
    remove: {
        borderRadius: 0,
        padding: '0 20px',
    },
    progressBarBackgroundColor: {
        backgroundColor: 'red',
    },
};

// convert JSON to nice HTML 
// the keys are the headers <h3> 
// the values are the values <p>
export function convertJSONToHTML(json) {
    let html = '';
    for (const [key, value] of Object.entries(json)) {
        const beautifiedKey = beautifyKey(key);
        html += `<h3>${beautifiedKey}</h3><p>${value}</p>`;
    }
    return html;
}

export function beautifyKey(key) {
    // Handle sequences of uppercase letters as acronyms
    // This regex splits on word boundaries while preserving acronyms
    const parts = key.split(/(?<=[a-z])(?=[A-Z])|(?<=[A-Z])(?=[A-Z][a-z])/);
    
    const beautifiedParts = parts.map(part => {
        // If the part is all uppercase (acronym), keep it as is
        if (part === part.toUpperCase() && part.length > 1) {
            return part;
        }
        // Otherwise, capitalize first letter and lowercase the rest
        return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    });
    
    return beautifiedParts.join(' ');
}


export function extractBase64FromDataUri(dataUri) {
    const base64Data = dataUri.split(',')[1]; // Split at the comma and get the second part
    return base64Data;
}

// convert keys to camelCase
export function convertKeysToCamelCase(obj) {
    const newObj = {};
    for (const key in obj) {
        const newKey = key.charAt(0).toLowerCase() + key.slice(1);
        newObj[newKey] = obj[key];
    }
    return newObj;
}


// extract gtin expiry date and serial number from the barcode
export function parseBarcode(rawBarcode) {
    // barcode format: 010541473451018917231231216506715
    // gtin: 05414734510189
    try {
        if (!rawBarcode) {
            return {};
        }
        // gtin is from the 3rd character to the 17th character
        const gtinMatch = rawBarcode.match(/(?<=^.{2})(.{14})/);
        // expiry date is from the 19th character to the 24th character
        const expiryMatch = rawBarcode.match(/(?<=^.{18})(.{6})/);
        // serial number is from the 22nd character to the end
        const serialMatch = rawBarcode.match(/(?<=21)(\d+)/);


        let gtin, expiry, serial;

        if (gtinMatch) {
            gtin = gtinMatch[0];
        }

        if (expiryMatch) {
            const expiryDate = expiryMatch[0];
            const year = `20${expiryDate.slice(0, 2)}`;
            const month = expiryDate.slice(2, 4);
            const day = expiryDate.slice(4, 6);
            const dateObj = new Date(year, month - 1, day);
            if (isNaN(dateObj.getTime())) {
                // replace the expiry date with the current date
                const currentDate = new Date();
                expiry = currentDate.toISOString();
            }
            expiry = dateObj.toISOString();
        }

        if (serialMatch) {
            serial = serialMatch[0];
        }

        // if gtin is null then use the raw barcode
        gtin = gtin ? gtin : rawBarcode;

        return { gtin, expiry, serial };
    } catch (error) {
        return { gtin: rawBarcode, expiry: null, serial: null };
    }
}

// lowecase the first letter of a string
export function lowerCaseFirstLetter(string) {
    return string.charAt(0).toLowerCase() + string.slice(1);
}

/** Matches System.Text.Json JsonNamingPolicy.CamelCase (first character only). */
/**
 * Ports System.Text.Json's JsonNamingPolicy.CamelCase exactly (see corefx JsonNamingPolicy.ToCamelCase):
 * lowercases a leading run of capitals but keeps the last one if it starts the next word,
 * e.g. NHSNumber -> nhsNumber, CHINumber -> chiNumber, Id -> id, FirstName -> firstName.
 * A naive "just lowercase the first char" breaks any DTO field with a 2+ letter acronym prefix
 * (NHSNumber/CHINumber), silently desyncing entity[key] lookups from the real API response shape.
 */
export function convertKeyToCamelCase(key) {
    if (!key) return key;
    const chars = String(key).split('');
    if (!/[A-Z]/.test(chars[0])) return key;
    for (let i = 0; i < chars.length; i++) {
        const isUpper = /[A-Z]/.test(chars[i]);
        if (i === 1 && !isUpper) break;
        const hasNext = i + 1 < chars.length;
        if (i > 0 && hasNext && !/[A-Z]/.test(chars[i + 1])) {
            if (chars[i + 1] === ' ') chars[i] = chars[i].toLowerCase();
            break;
        }
        chars[i] = chars[i].toLowerCase();
    }
    return chars.join('');
}

/**
 * Unique SFTP-safe filename: <sanitized stem>-<suffix><ext>
 * `suffix` should be the patient identifier — every upload requires a selected patient,
 * so callers must pass it. Falls back to Date.now() only if a caller omits it entirely
 * (legacy safety net; should not happen in the app's own upload flow).
 */
function sanitizeUploadStem(name) {
    return String(name || '').replace(/[^a-zA-Z0-9._-]/g, '_') || 'upload';
}

/** One or more trailing Date.now() millis (13 digits). Leaves study-date fragments like `-15-01-32` alone. */
const UPLOAD_TIMESTAMP_SUFFIX = /(-\d{13})+(?=\.[^./]+$)/;

/**
 * Display name for uploaded / generated files: drop `-1787671333214` (and stacked repeats)
 * and, when known, put the patient identifier in its place. SFTP path is unchanged.
 * Only needed for files uploaded before the patient identifier became the suffix.
 */
export function formatUploadDisplayName(fileName, patientIdentifier) {
    if (fileName == null || fileName === '') return '';
    const id = String(patientIdentifier || '').trim();
    return String(fileName).replace(UPLOAD_TIMESTAMP_SUFFIX, id ? `-${id}` : '');
}

export function buildUploadFileName(originalName, suffix = Date.now()) {
    const raw = String(originalName || '');
    const extMatch = raw.match(/(\.[A-Za-z0-9]+)$/);
    const ext = extMatch ? extMatch[1].toLowerCase() : '.bin';
    const stem = extMatch ? raw.slice(0, -extMatch[1].length) : raw;
    return `${sanitizeUploadStem(stem)}-${suffix}${ext}`;
}

/** Study zips always end in .zip (strips the original extension first). */
export function buildUploadZipFileName(originalName, suffix = Date.now()) {
    const baseName = String(originalName || '')
        .replace(/\.[^/.]+$/, '')
        .replace(/[^a-zA-Z0-9._-]/g, '_') || 'upload';
    return `${baseName}-${suffix}.zip`;
}

/** Format API date/datetime values for UK display (DD/MM/YYYY, or with time when present). */
export function formatUkDateTime(value) {
    if (value === null || value === undefined || value === '') return '-';
    const parsed = dayjs(value);
    if (!parsed.isValid()) return String(value);
    // DateTime.MinValue (0001) — JS engines often parse that as 1901-01-01.
    const y = parsed.year();
    if (y < 100 || (y === 1901 && parsed.month() === 0 && parsed.date() === 1)) return '-';
    const hasTime = parsed.hour() !== 0
        || parsed.minute() !== 0
        || parsed.second() !== 0
        || parsed.millisecond() !== 0;
    return hasTime ? parsed.format('DD/MM/YYYY HH:mm') : parsed.format('DD/MM/YYYY');
}

/** DD/MM/YYYY only, no time — extracts the date part first to dodge timezone shifts. */
export function formatDateOnly(value) {
    if (!value) return '';
    const dateStr = typeof value === 'string' && value.includes('T') ? value.split('T')[0] : value;
    const parsed = dayjs(dateStr);
    return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '';
}

/** User-facing delete error: falls back to a generic message for opaque API errors. */
export function formatDeleteErrorDetail(t, error) {
    const raw = String(error?.response?.data?.detail ?? error?.message ?? '').trim();
    if (!raw || /^An error occurred while deleting\b/i.test(raw)) {
        return t('Failed to delete. It may still be referenced by other records.');
    }
    return raw;
}

/** Read DTO field metadata whether the API used camelCase or PascalCase keys. */
export function getDtoFieldProp(fieldProps, camelKey, pascalKey) {
    if (!fieldProps) return undefined;
    if (fieldProps[camelKey] !== undefined) return fieldProps[camelKey];
    if (fieldProps[pascalKey] !== undefined) return fieldProps[pascalKey];
    return undefined;
}

export function isDateTimeDtoField(fieldProps) {
    const rawType = getDtoFieldProp(fieldProps, 'type', 'Type');
    return typeof rawType === 'string' && rawType.toLowerCase() === 'datetime';
}

export function looksLikeIsoDateTime(value) {
    if (typeof value !== 'string') return false;
    return /^\d{4}-\d{2}-\d{2}(?:T|\b)/.test(value.trim());
}

export function formatTableCellValue(value) {
    if (looksLikeIsoDateTime(value)) return formatUkDateTime(value);
    if (value === null || value === undefined || value === '') return '-';
    return value;
}


/**
 * Converts a barcode value to a base64 encoded image string.
 * 
 * @param {string} value - The barcode value to encode.
 * @param {Object} options - JsBarcode options (optional).
 * @param {string} options.format - The barcode format (e.g., 'CODE128', 'EAN13').
 * @param {number} options.width - The width of a single bar.
 * @param {number} options.height - The height of the barcode.
 * @param {number} options.fontSize - The font size of the text.
 * @param {string} options.background - The background color (default: '#ffffff').
 * @param {string} options.lineColor - The color of the bars (default: '#000000').
 * @returns {Promise<string>} A promise that resolves with the base64 encoded image string.
 */
export function barcodeToBase64(value, options = {}) {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    
    try {
      JsBarcode(canvas, value, {
        format: 'CODE128',
        width: 2,
        height: 100,
        fontSize: 20,
        background: '#ffffff',
        lineColor: '#000000',
        ...options
      });
      
      const base64 = canvas.toDataURL('image/png');
      resolve(base64);
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Enhanced filter builder with flexible options and better validation
 */

// Configuration object for default behaviors
const DEFAULT_CONFIG = {
  resetPage: true,
  defaultFilter: 'subjectIdentifier',
  separator: ',',
  trimValues: true,
  allowEmpty: false
};

/**
 * Parses filter strings into arrays, handling edge cases
 * @private
 */
function parseFilters(filterStr, queryStr, config = DEFAULT_CONFIG) {
  if (!filterStr || !queryStr) {
    return { filters: [], queries: [] };
  }

  const filters = filterStr.split(config.separator);
  const queries = queryStr.split(config.separator);

  return {
    filters: config.trimValues ? filters.map(f => f.trim()) : filters,
    queries: config.trimValues ? queries.map(q => q.trim()) : queries
  };
}

/**
 * Validates that filters and queries arrays are properly matched
 * @private
 */
function validateFilterQueries(filters, queries) {
  if (filters.length !== queries.length) {
    console.warn(`Filter/query mismatch: ${filters.length} filters, ${queries.length} queries`);
    return false;
  }
  return true;
}

/**
 * Builds multiple filters from an object
 * @private
 */
function buildMultipleFilters(existingFilters, filterObject, config) {
  let result = existingFilters;
  
  for (const [key, value] of Object.entries(filterObject)) {
    if (value !== null && value !== undefined && (config.allowEmpty || value !== '')) {
      result = filterBuilder(result, key, String(value), { 
        ...config, 
        combine: true 
      });
    }
  }
  
  return result;
}

/**
 * Builds filter parameters for API requests with enhanced flexibility
 * 
 * @param {Object} existingFilters - Current filter state object
 * @param {string|Object} newFilter - New filter property name OR filter object
 * @param {string} newQuery - New filter query value (ignored if newFilter is object)
 * @param {Object} options - Configuration options
 * @param {boolean} options.combine - Whether to combine with existing filters (default: true)
 * @param {boolean} options.replace - Whether to replace existing filter if it exists (default: true)
 * @param {boolean} options.resetPage - Whether to reset page to 1 (default: true)
 * @param {boolean} options.allowEmpty - Whether to allow empty query values (default: false)
 * @param {string} options.defaultFilter - Default filter to use when all filters removed
 * @returns {Object} Updated filter object
 */
export function filterBuilder(existingFilters = {}, newFilter, newQuery = '', options = {}) {
  const config = { ...DEFAULT_CONFIG, ...options };
  
  // Handle object-style filter input: { name: 'john', status: 'active' }
  if (typeof newFilter === 'object' && newFilter !== null) {
    return buildMultipleFilters(existingFilters, newFilter, config);
  }

  // Validate inputs
  if (!newFilter || (typeof newFilter !== 'string')) {
    console.warn('Invalid filter provided to filterBuilder');
    return existingFilters;
  }

  if (!config.allowEmpty && !newQuery) {
    console.warn('Empty query provided to filterBuilder');
    return existingFilters;
  }

  // Single filter or no combination
  if (!config.combine || !existingFilters.filter || !existingFilters.query) {
    return {
      ...existingFilters,
      filter: newFilter,
      query: newQuery,
      ...(config.resetPage && { page: 1 })
    };
  }

  // Parse existing filters
  const { filters, queries } = parseFilters(existingFilters.filter, existingFilters.query, config);
  
  if (!validateFilterQueries(filters, queries)) {
    // Fallback to single filter on validation error
    return {
      ...existingFilters,
      filter: newFilter,
      query: newQuery,
      ...(config.resetPage && { page: 1 })
    };
  }

  // Find existing filter index
  const existingIndex = filters.indexOf(newFilter);
  
  if (existingIndex !== -1) {
    if (config.replace) {
      // Update existing filter
      queries[existingIndex] = newQuery;
    } else {
      // Don't update if replace is false
      return existingFilters;
    }
  } else {
    // Add new filter
    filters.push(newFilter);
    queries.push(newQuery);
  }

  return {
    ...existingFilters,
    filter: filters.join(config.separator),
    query: queries.join(config.separator),
    ...(config.resetPage && { page: 1 })
  };
}

/**
 * Removes specific filters from combined filters with flexible options
 * 
 * @param {Object} existingFilters - Current filter state object
 * @param {string|string[]} filtersToRemove - Filter name(s) to remove
 * @param {Object} options - Configuration options
 * @param {string} options.defaultFilter - Fallback filter when all removed
 * @param {string} options.defaultQuery - Fallback query when all removed
 * @param {boolean} options.resetPage - Whether to reset page to 1
 * @returns {Object} Updated filter object
 */
export function removeFilter(existingFilters = {}, filtersToRemove, options = {}) {
  const config = { ...DEFAULT_CONFIG, defaultQuery: '', ...options };
  
  if (!existingFilters.filter || !existingFilters.query) {
    return existingFilters;
  }

  const { filters, queries } = parseFilters(existingFilters.filter, existingFilters.query, config);
  
  if (!validateFilterQueries(filters, queries)) {
    return existingFilters;
  }

  // Handle multiple filters to remove
  const removeList = Array.isArray(filtersToRemove) ? filtersToRemove : [filtersToRemove];
  
  // Remove filters in reverse order to maintain indices
  const indicesToRemove = removeList
    .map(filter => filters.indexOf(filter))
    .filter(index => index !== -1)
    .sort((a, b) => b - a); // Sort descending

  indicesToRemove.forEach(index => {
    filters.splice(index, 1);
    queries.splice(index, 1);
  });

  // Handle empty result
  if (filters.length === 0) {
    return {
      ...existingFilters,
      filter: config.defaultFilter,
      query: config.defaultQuery,
      ...(config.resetPage && { page: 1 })
    };
  }

  return {
    ...existingFilters,
    filter: filters.join(config.separator),
    query: queries.join(config.separator),
    ...(config.resetPage && { page: 1 })
  };
}

/**
 * Gets filter values with enhanced querying capabilities
 * 
 * @param {Object} existingFilters - Current filter state object
 * @param {string|string[]} filterNames - Filter name(s) to get values for
 * @returns {string|Object|null} Single value, object of values, or null
 */
export function getFilterValue(existingFilters = {}, filterNames) {
  if (!existingFilters.filter || !existingFilters.query) {
    return Array.isArray(filterNames) ? {} : null;
  }

  const { filters, queries } = parseFilters(existingFilters.filter, existingFilters.query);
  
  if (!validateFilterQueries(filters, queries)) {
    return Array.isArray(filterNames) ? {} : null;
  }

  // Handle multiple filter names
  if (Array.isArray(filterNames)) {
    const result = {};
    filterNames.forEach(name => {
      const index = filters.indexOf(name);
      result[name] = index !== -1 ? queries[index] : null;
    });
    return result;
  }

  // Single filter name
  const index = filters.indexOf(filterNames);
  return index !== -1 ? queries[index] : null;
}

/**
 * Clears all filters and optionally sets new ones
 * 
 * @param {Object} existingFilters - Current filter state object
 * @param {Object} newFilters - Optional new filters to set
 * @param {Object} options - Configuration options
 * @returns {Object} Updated filter object
 */
export function clearFilters(existingFilters = {}, newFilters = null, options = {}) {
  const config = { ...DEFAULT_CONFIG, ...options };
  
  const baseResult = {
    ...existingFilters,
    filter: config.defaultFilter,
    query: '',
    ...(config.resetPage && { page: 1 })
  };

  if (newFilters) {
    return buildMultipleFilters(baseResult, newFilters, config);
  }

  return baseResult;
}

/**
 * Checks if a specific filter exists
 * 
 * @param {Object} existingFilters - Current filter state object
 * @param {string} filterName - Filter name to check
 * @returns {boolean} Whether the filter exists
 */
export function hasFilter(existingFilters = {}, filterName) {
  if (!existingFilters.filter) {
    return false;
  }

  const { filters } = parseFilters(existingFilters.filter, existingFilters.query || '');
  return filters.includes(filterName);
}

/**
 * Gets all current filters as an object
 * 
 * @param {Object} existingFilters - Current filter state object
 * @returns {Object} Object with filter names as keys and queries as values
 */
export function getAllFilters(existingFilters = {}) {
  if (!existingFilters.filter || !existingFilters.query) {
    return {};
  }

  const { filters, queries } = parseFilters(existingFilters.filter, existingFilters.query);
  
  if (!validateFilterQueries(filters, queries)) {
    return {};
  }

  const result = {};
  filters.forEach((filter, index) => {
    result[filter] = queries[index];
  });
  
  return result;
}

/**
 * Toggles a filter (adds if not present, removes if present)
 * 
 * @param {Object} existingFilters - Current filter state object
 * @param {string} filterName - Filter name to toggle
 * @param {string} queryValue - Query value to use when adding
 * @param {Object} options - Configuration options
 * @returns {Object} Updated filter object
 */
export function toggleFilter(existingFilters = {}, filterName, queryValue = '', options = {}) {
  if (hasFilter(existingFilters, filterName)) {
    return removeFilter(existingFilters, filterName, options);
  } else {
    return filterBuilder(existingFilters, filterName, queryValue, { combine: true, ...options });
  }
}

// Export configuration for external customization
export { DEFAULT_CONFIG };